import {
  IonButton,
  IonButtons,
  IonFooter,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonActionSheet,
  useIonRouter,
} from '@ionic/react';
import { globe, lockClosed } from 'ionicons/icons';
import React, { forwardRef, ReactElement, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { t } from 'ttag';

import BackButton from '@components/BackButton';
import RoutePath from '@custom-types/RoutePath';
import { generateRoute } from '@services/navigation-service';
import { getEventInfo } from '@state/event';
import { getLocale } from '@state/system';

const Page = styled(IonPage)`
  background-size: cover;
  background-position: center;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-bottom: 16px;
  margin: 0px 16px;
`;

const LanguageIcon = styled(IonIcon)`
  margin-right: 5px;
`;

const Footer = styled(IonFooter)`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 16px;
  color: var(--ion-text-color);
`;

export enum LockMode {
  None,
  Lock,
  Unlock,
}

export interface Props {
  // eslint-disable-next-line react/require-default-props
  footer?: string | ReactElement;
  // eslint-disable-next-line react/require-default-props
  backgroundImage?: string | null;
  // eslint-disable-next-line react/require-default-props
  lockMode?: LockMode;
  // eslint-disable-next-line react/require-default-props
  wrapContent?: boolean;
  // eslint-disable-next-line react/require-default-props
  disableBack?: boolean;
  children: ReactNode;
}

// At some point when we remove onsite_badge_printing feature flag, the code would be more simplified and we can
// combine @components/KioskLayout and @components/Layout into one component
const KioskLayout = forwardRef<HTMLDivElement, Props>(
  (
    {
      children,
      footer = undefined,
      backgroundImage = undefined,
      lockMode = LockMode.Lock,
      wrapContent = true,
      disableBack = false,
    },
    ref
  ) => {
    const { name: eventName } = useSelector(getEventInfo);
    const { push: pushRoute } = useIonRouter();
    const locale = useSelector(getLocale);
    const [present, dismiss] = useIonActionSheet();

    const unlock = () => {
      present({
        header: t`Unlock Device`,
        buttons: [
          {
            text: t`Enter Password`,
            data: { route: generateRoute(RoutePath.KioskUnlockWithPassword) },
          },
          {
            text: t`Enter Access Code`,
            data: { route: generateRoute(RoutePath.KioskUnlockWithCode) },
          },
          {
            text: t`Cancel`,
            role: 'cancel',
          },
        ],
        onWillDismiss: (ev: CustomEvent) => {
          if (ev.detail.role === 'cancel') {
            dismiss();
          } else {
            pushRoute(ev.detail.data.route, 'forward');
          }
        },
      });
    };

    const background = backgroundImage && lockMode !== LockMode.Unlock ? backgroundImage : '';

    return (
      <Page ref={ref} style={{ backgroundImage: background }}>
        <IonHeader className="ion-no-border">
          <IonToolbar>
            <IonButtons slot="start">
              {!disableBack && lockMode === LockMode.Lock && (
                <IonButton
                  // skipcq: JS-0417
                  onClick={unlock}
                >
                  <IonIcon icon={lockClosed} />
                </IonButton>
              )}
              {(lockMode === LockMode.Unlock || disableBack) && <BackButton />}
            </IonButtons>
            <IonTitle>{eventName}</IonTitle>
            {lockMode === LockMode.Lock && (
              <IonButtons slot="end">
                <IonButton fill="clear" routerLink={generateRoute(RoutePath.Language)}>
                  <LanguageIcon icon={globe} />
                  {locale.toUpperCase()}
                </IonButton>
              </IonButtons>
            )}
          </IonToolbar>
        </IonHeader>

        {wrapContent ? <Content style={{ flexGrow: 1 }}>{children}</Content> : children}
        {footer && <Footer className="ion-no-border">{footer}</Footer>}
      </Page>
    );
  }
);

export default KioskLayout;
